import React, { useEffect, useState, useCallback, useRef } from "react";
import Styles from "./SelfListing.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  emptyCounterPartyList,
  fetchCounterPartList,
  fetchLedgerBids,
  fetchMainScreenActiveConnection,
  setActiveListingId,
} from "../../../../Redux/Slices/CapacitySlice";
import { Add } from "@mui/icons-material";
import emptySelfListing from "../../../../assets/images/emptySelfListing.png";
import SelfListingBox from "./SelfListingBox";
import AvailableListingSkeleton from "../../../../commons/AvailableListingSkeleton";
import ViewInjectionModal from "../../../Modals/ConGen/ViewInjectionModal/ViewInjectionModal";
import ConGenListingModal from "../../../Modals/ConGen/ConGenListingModal";
import { createListing } from "../../../../Services/BuySell/Creation";

const EmptyState = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state?.persistedReducer?.userType);

  const handleModalSubmit = async (data) => {
    setLoading(true);
    let res = await createListing(
      data,
      user === "CONSUMER" ? "consumer" : "generator",
    );
    if (res?.data?.code === 200) {
      setOpen(false);
      window.location.reload();
    }
    setLoading(false);
  };

  return (
    <>
      {" "}
      {open && (
        <ConGenListingModal
          open={open}
          loading={loading}
          type="add"
          handleClose={() => setOpen(false)}
          handleSubmit={handleModalSubmit}
        />
      )}
      <div className={Styles.emptyState}>
        <img
          style={{ marginBottom: "10px" }}
          src={emptySelfListing}
          alt="No Available Listings"
        />
        <h2>You have not listed your </h2>
        <h2>requirements</h2>
        <p>
          There are no counterparties that match your requirements. We’ll inform
          you as soon as a match is found
        </p>

        <div>
          {" "}
          <div
            className={`activeButton_type_2 display_flex_row_center ${Styles.addListingButton}`}
            onClick={() => setOpen(true)}
          >
            <Add style={{ fontSize: "16px" }} />
            <span>Add Listing</span>
          </div>
        </div>
      </div>
    </>
  );
};

const SelfListing = () => {
  const dispatch = useDispatch();
  const loading = useSelector(
    (state) => state?.CapacitySlice?.fetchSelfCapacityLoading,
  );
  const refreshTrigger = useSelector(
    (state) => state?.CapacitySlice?.refreshTrigger,
  );
  const [indentsModal, setIndentsModal] = useState(false);

  const handleOpenIndent = (data) => {
    dispatch(setActiveListingId(data.id));
    setIndentsModal(true);
  };

  const handleCloseIndent = () => {
    setIndentsModal(false);
  };

  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole,
  )?.toLowerCase();

  const list = useSelector((state) => {
    return state?.CapacitySlice?.list;
  });

  // Track user changes with a ref to detect role switches
  const prevUserRef = useRef(user);

  const [activeId, setActiveId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const activeListingId = useSelector(
    (state) => state?.CapacitySlice?.activeListingId,
  );
  const listingRefs = useRef({});

  // Use ref to track latest user value for callbacks
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const handleActiveConnection = useCallback(
    (id) => {
      const payload = {
        id: id,
        energySourceType: "",
        energyFuelType: "",
        tag: "",
      };

      // Use ref to get latest user value
      dispatch(
        fetchCounterPartList({ user: userRef.current, payload, curr: 0 }),
      );
      dispatch(fetchMainScreenActiveConnection({ capId: id }));
    },
    [dispatch],
  );

  useEffect(() => {
    // Check if user role has changed
    if (prevUserRef.current !== user) {
      // Role switched - reset activeId and wait for new data
      setActiveId(null);
      dispatch(setActiveListingId(null));
      prevUserRef.current = user;
      return;
    }

    if (activeId && user && list?.length > 0) {
      handleActiveConnection(activeId);
    }
  }, [activeId, handleActiveConnection, list, user, dispatch]);

  const getFilteredList = useCallback(() => {
    if (activeFilter === "ALL") return list;
    if (activeFilter === "ACCEPTED") {
      // Show TERMS_ACCEPTED for ACCEPTED filter
      return list?.filter((item) => item.status === "TERMS_ACCEPTED");
    }

    if (
      activeFilter === "PLACED" ||
      activeFilter === "TERMS_INITIATED" ||
      activeFilter === "RECEIVED" ||
      activeFilter === "ACTIVE"
    ) {
      return list?.filter(
        (item) =>
          item.status === "ACTIVE" ||
          item.status === "PLACED" ||
          item.status === "TERMS_INITIATED" ||
          item.status === "TERMS_INITIATE_AND_ACCEPT" ||
          item.status === "RECEIVED",
      );
    }
    if (activeFilter === "ONGOING") {
      // Show Final for ONGOING filter
      return list?.filter((item) => item.status === "ONGOING");
    }

    return list?.filter((item) => item.status?.toUpperCase() === activeFilter);
  }, [activeFilter, list]);

  const id = useSelector(
    (state) =>
      state?.persistedReducer?.user["activeGeneratorId"] ||
      state?.persistedReducer?.user["activeConsumerId"],
  );

  useEffect(() => {
    if (activeListingId) {
      // Scroll to the active listing
      setTimeout(() => {
        const ref = listingRefs.current[activeListingId];
        if (ref && ref.scrollIntoView) {
          ref.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      setActiveId(activeListingId);

      return;
    }
    // Only set activeId from list if we don't have one and list is available
    if (getFilteredList()?.length > 0 && id) {
      setActiveId(getFilteredList()[0]?.id);
      dispatch(setActiveListingId(getFilteredList()[0]?.id));
    }
  }, [activeListingId, dispatch, getFilteredList, id, user]);

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  useEffect(() => {
    const filtered = getFilteredList();

    if (filtered && filtered.length > 0) {
      const data = filtered[0];
      if (activeFilter === "ALL") {
        if (activeListingId) {
          setActiveId(activeListingId);

          // Scroll to the active listing
          setTimeout(() => {
            const ref = listingRefs.current[activeListingId];
            if (ref && ref.scrollIntoView) {
              ref.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }, 100);

          return;
        }
        if (list?.length > 0) {
          setActiveId(list[0]?.id);
          dispatch(setActiveListingId(list[0]?.id));
        }
        return;
      }
      dispatch(setActiveListingId(data?.id));
      setActiveId(data?.id);
      dispatch(fetchLedgerBids(data?.id));

      // Fetch counter party list when filter changes
      const payload = {
        id: data?.id,
        energySourceType: "",
        energyFuelType: "",
        tag: "",
      };
      dispatch(fetchCounterPartList({ user, payload, curr: 0 }));
    } else if (filtered?.length === 0 && loading === false) {
      dispatch(emptyCounterPartyList());
      setActiveId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, user, refreshTrigger, getFilteredList, dispatch]);

  return (
    <>
      {indentsModal && (
        <ViewInjectionModal
          open={indentsModal}
          handleClose={handleCloseIndent}
          data={list?.find((item) => item.id === activeId)}
        />
      )}

      <div className={Styles.sortSection}>
        {["ALL", "ACTIVE", "HOLD", "ACCEPTED", "ONGOING"].map((filter) => {
          let label = filter;
          label = filter.charAt(0) + filter.slice(1).toLowerCase();
          return (
            <div
              key={filter}
              className={`${Styles.filterButton} ${
                activeFilter === filter ? Styles.activeFilter : ""
              }`}
              onClick={() => handleFilterClick(filter)}
            >
              {label}
            </div>
          );
        })}
      </div>
      {loading ? (
        <AvailableListingSkeleton count={4} />
      ) : (
        <>
          {getFilteredList()?.length === 0 ? (
            <div style={{ position: "relative", height: "100%" }}>
              <EmptyState />
            </div>
          ) : (
            getFilteredList()?.map((ele) => (
              <SelfListingBox
                key={ele.id}
                data={ele}
                setActiveId={setActiveId}
                alterButton={true}
                activeId={activeId}
                isActive={activeId === ele.id}
                handleOpenIndent={handleOpenIndent}
                ref={(ref) => (listingRefs.current[ele.id] = ref)}
              />
            ))
          )}
        </>
      )}
    </>
  );
};

export default SelfListing;
